/**
 * Scene topic + artifact + audio timelines with minimum non-overlapping lanes.
 */

import { mergeSegmentsIntoRendered } from './mergeRendered';
import {
  elementNarrationEnd,
  elementNarrationStart,
  elementVisibleStart,
  narratedBlocksInOrder,
  sceneAtPlayhead,
} from './layout';
import { assignMinimumLanes } from './timelineLanes';
import {
  blockAudioLabel,
  blockLaneColor,
} from './sceneAudioTimeline';

const SKIP_AUDIO_TYPES = new Set(['scene_transition', 'code_highlight', 'flowchart_arrow']);

const ARTIFACT_TYPES = new Set([
  'scene_title',
  'bullet',
  'explanation',
  'keyword',
  'image',
  'code_step',
  'console_output',
  'flowchart_node',
  'flowchart_arrow',
]);

const ARTIFACT_COLORS = {
  scene_title: 'bg-emerald-700/75 border-emerald-500/60 text-emerald-50',
  bullet: 'bg-sky-700/75 border-sky-500/60 text-sky-50',
  explanation: 'bg-violet-700/75 border-violet-500/60 text-violet-50',
  image: 'bg-amber-700/80 border-amber-500/70 text-amber-50',
  code_step: 'bg-slate-600/80 border-slate-400/60 text-slate-100',
  default: 'bg-slate-600/70 border-slate-500/50 text-slate-100',
};

function pct(time, duration) {
  if (!duration || duration <= 0) return 0;
  return Math.max(0, Math.min(100, (time / duration) * 100));
}

function artifactColor(type, role) {
  if (role === 'visual' || type === 'image') return ARTIFACT_COLORS.image;
  return ARTIFACT_COLORS[type] || ARTIFACT_COLORS.default;
}

function hasSpokenAudio(el) {
  const type = (el?.type || '').trim();
  if (SKIP_AUDIO_TYPES.has(type)) return false;
  return String(el?.narration || '').trim().length > 0;
}

function isArtifact(el) {
  return ARTIFACT_TYPES.has((el?.type || '').trim());
}

function clipFromElement(el, scene, sceneIndex, duration, kind) {
  const sceneRef = scene || null;
  const start =
    kind === 'artifact'
      ? elementVisibleStart(el, sceneRef)
      : elementNarrationStart(el);
  const end =
    kind === 'audio'
      ? elementNarrationEnd(el)
      : Number(el?.end ?? elementNarrationEnd(el));
  const safeEnd = Math.max(start + 0.05, end);

  return {
    id: el.id,
    sceneIndex,
    sceneId: scene?.id,
    type: el.type,
    segmentRole: el.segment_role,
    segmentIndex: el.segment_index,
    label: kind === 'audio' ? blockAudioLabel(el) : blockAudioLabel(el),
    colorClass: kind === 'audio' ? blockLaneColor(el) : artifactColor(el.type, el.segment_role),
    topic: el.slide_context?.topic || el.content?.slice?.(0, 40),
    start,
    end: safeEnd,
    duration: safeEnd - start,
    leftPct: pct(start, duration),
    widthPct: Math.max(0.35, pct(safeEnd, duration) - pct(start, duration)),
    narrationPreview: String(el.narration || el.content || '').slice(0, 80),
    lane: 0,
  };
}

function buildFromRendered(rendered, segments, duration) {
  const total = Math.max(0.001, Number(duration) || rendered?.total_duration || 0.001);
  const merged = segments?.length
    ? mergeSegmentsIntoRendered(rendered, segments)
    : rendered;
  const elements = merged?.elements || [];
  const byId = Object.fromEntries(elements.filter((e) => e.id).map((e) => [e.id, e]));
  const scenesMeta = [];

  const sceneNodes = [];
  const audioPool = [];
  const artifactPool = [];

  for (const sc of merged?.scenes || []) {
    const sceneIndex = scenesMeta.length;
    const bids = sc.block_ids || [];
    const titleEl = bids.map((id) => byId[id]).find((e) => e?.type === 'scene_title');
    const topic =
      titleEl?.content
      || segments?.[sceneIndex]?.title
      || `Scene ${sceneIndex + 1}`;
    const sceneStart = Number(sc.start) || 0;
    const sceneEnd = Number(sc.end) || sceneStart;

    sceneNodes.push({
      id: sc.id || `scene-${sceneIndex + 1}`,
      sceneIndex,
      label: topic,
      start: sceneStart,
      end: sceneEnd,
      leftPct: pct(sceneStart, total),
      widthPct: Math.max(0.5, pct(sceneEnd, total) - pct(sceneStart, total)),
      lane: 0,
    });

    const sceneAudio = [];
    const sceneArtifacts = [];

    for (const bid of bids) {
      const el = byId[bid];
      if (!el) continue;
      const sceneCtx = { ...sc, start: sceneStart, end: sceneEnd };

      if (hasSpokenAudio(el)) {
        const clip = clipFromElement(el, sceneCtx, sceneIndex, total, 'audio');
        sceneAudio.push(clip);
        audioPool.push(clip);
      }
      if (isArtifact(el)) {
        const clip = clipFromElement(el, sceneCtx, sceneIndex, total, 'artifact');
        sceneArtifacts.push(clip);
        artifactPool.push(clip);
      }
    }

    sceneAudio.sort((a, b) => a.start - b.start || (a.segmentIndex ?? 0) - (b.segmentIndex ?? 0));
    sceneArtifacts.sort((a, b) => a.start - b.start || (a.segmentIndex ?? 0) - (b.segmentIndex ?? 0));

    const lastNarrEnd = sceneAudio.length
      ? Math.max(...sceneAudio.map((b) => b.end))
      : sceneStart;
    const gap =
      sceneEnd > lastNarrEnd + 0.04
        ? {
            start: lastNarrEnd,
            end: sceneEnd,
            leftPct: pct(lastNarrEnd, total),
            widthPct: Math.max(0.25, pct(sceneEnd, total) - pct(lastNarrEnd, total)),
          }
        : null;

    scenesMeta.push({
      sceneIndex,
      id: sc.id,
      title: topic,
      start: sceneStart,
      end: sceneEnd,
      leftPct: pct(sceneStart, total),
      widthPct: Math.max(0.5, pct(sceneEnd, total) - pct(sceneStart, total)),
      audioClips: sceneAudio,
      artifactClips: sceneArtifacts,
      gap,
      blockCount: sceneAudio.length,
    });
  }

  const audioPacked = assignMinimumLanes(audioPool);
  const artifactPacked = assignMinimumLanes(artifactPool);

  const audioByLane = Array.from({ length: audioPacked.laneCount }, () => []);
  for (const item of audioPacked.items) {
    audioByLane[item.lane].push(item);
  }

  const artifactByLane = Array.from({ length: artifactPacked.laneCount }, () => []);
  for (const item of artifactPacked.items) {
    artifactByLane[item.lane].push(item);
  }

  return {
    duration: total,
    scenes: scenesMeta,
    sceneNodes: { laneCount: 1, lanes: [sceneNodes] },
    audio: { laneCount: audioPacked.laneCount, lanes: audioByLane, items: audioPacked.items },
    artifacts: {
      laneCount: artifactPacked.laneCount,
      lanes: artifactByLane,
      items: artifactPacked.items,
    },
  };
}

function buildFromSegments(segments, duration) {
  const total = Math.max(0.001, Number(duration) || 0.001);
  const sceneNodes = [];
  const audioPool = [];
  const artifactPool = [];
  const scenesMeta = [];

  (segments || []).forEach((seg, sceneIndex) => {
    const sceneStart = Number(seg.start_time) || 0;
    const sceneEnd = Number(seg.end_time) || sceneStart;
    const topic = seg.title || `Scene ${sceneIndex + 1}`;

    sceneNodes.push({
      id: seg.id,
      sceneIndex,
      label: topic,
      start: sceneStart,
      end: sceneEnd,
      leftPct: pct(sceneStart, total),
      widthPct: Math.max(0.5, pct(sceneEnd, total) - pct(sceneStart, total)),
      lane: 0,
    });

    const sceneAudio = [];
    const sceneArtifacts = [];

    (seg.blocks || []).forEach((b, i) => {
      const el = {
        id: b.block_id,
        type: b.type,
        segment_role: b.segment_role,
        segment_index: b.segment_index,
        narration: b.narration,
        content: b.screen_text,
        slide_context: b.slide_context,
        sync_visual_at_scene_start: b.sync_visual_at_scene_start,
        narration_start: b.narration_start,
        narration_end: b.narration_end,
        visible_from: b.visible_from,
        start: b.start,
        end: b.end,
        audio_duration: b.audio_duration,
      };
      const sceneCtx = { start: sceneStart, end: sceneEnd };

      if (hasSpokenAudio(el)) {
        const clip = clipFromElement(el, sceneCtx, sceneIndex, total, 'audio');
        sceneAudio.push(clip);
        audioPool.push(clip);
      }
      if (isArtifact(el)) {
        const clip = clipFromElement(el, sceneCtx, sceneIndex, total, 'artifact');
        sceneArtifacts.push(clip);
        artifactPool.push(clip);
      }
    });

    const lastNarrEnd = sceneAudio.length
      ? Math.max(...sceneAudio.map((x) => x.end))
      : sceneStart;

    scenesMeta.push({
      sceneIndex,
      id: seg.id,
      title: topic,
      start: sceneStart,
      end: sceneEnd,
      audioClips: sceneAudio,
      artifactClips: sceneArtifacts,
      gap:
        sceneEnd > lastNarrEnd + 0.04
          ? {
              start: lastNarrEnd,
              end: sceneEnd,
              leftPct: pct(lastNarrEnd, total),
              widthPct: Math.max(0.25, pct(sceneEnd, total) - pct(lastNarrEnd, total)),
            }
          : null,
      blockCount: sceneAudio.length,
    });
  });

  const audioPacked = assignMinimumLanes(audioPool);
  const artifactPacked = assignMinimumLanes(artifactPool);

  const audioByLane = Array.from({ length: audioPacked.laneCount }, () => []);
  audioPacked.items.forEach((item) => audioByLane[item.lane].push(item));

  const artifactByLane = Array.from({ length: artifactPacked.laneCount }, () => []);
  artifactPacked.items.forEach((item) => artifactByLane[item.lane].push(item));

  return {
    duration: total,
    scenes: scenesMeta,
    sceneNodes: { laneCount: 1, lanes: [sceneNodes] },
    audio: { laneCount: audioPacked.laneCount, lanes: audioByLane, items: audioPacked.items },
    artifacts: {
      laneCount: artifactPacked.laneCount,
      lanes: artifactByLane,
      items: artifactPacked.items,
    },
  };
}

/**
 * @param {object} opts
 * @param {object[]} opts.segments
 * @param {number} opts.duration
 * @param {object} [opts.rendered]
 */
export function buildSceneMappedTimeline({ segments, duration, rendered }) {
  if (rendered?.elements?.length && rendered?.scenes?.length) {
    return buildFromRendered(rendered, segments, duration);
  }
  return buildFromSegments(segments, duration);
}

/** Active scene / audio block at playhead — matches canvas (no early topic advance). */
export function activeAtPlayhead(mapped, playhead, elements = null, rendered = null) {
  const t = Number(playhead) || 0;
  let sceneIndex = -1;
  let blockIndex = -1;

  if (!elements?.length) {
    for (const sc of mapped.scenes || []) {
      for (let bi = 0; bi < (sc.audioClips || []).length; bi += 1) {
        const b = sc.audioClips[bi];
        if (t >= b.start && t < b.end) {
          return { sceneIndex: sc.sceneIndex, blockIndex: bi };
        }
      }
    }
    return { sceneIndex: -1, blockIndex: -1 };
  }

  const scenesArr = (rendered?.scenes || []).length
    ? rendered.scenes
    : (mapped.scenes || []).map((sc) => ({
        id: sc.id,
        start: sc.start,
        end: sc.end,
        block_ids: (sc.audioClips || []).map((c) => c.id),
      }));

  const activeScene = sceneAtPlayhead(scenesArr, t, elements, rendered);
  if (activeScene) {
    sceneIndex = mapped.scenes.findIndex(
      (s) => s.id === activeScene.id || (s.start === activeScene.start && s.end === activeScene.end),
    );
    if (sceneIndex < 0) {
      sceneIndex = mapped.scenes.findIndex((s) => t >= s.start && t < s.end);
    }
  }

  const order = narratedBlocksInOrder(elements, scenesArr);
  let activeEl = null;
  for (let i = 0; i < order.length; i += 1) {
    const el = order[i];
    const row = rendered?.narration_timings?.find((r) => r.element_id === el.id);
    const ns = row?.start != null ? Number(row.start) : elementNarrationStart(el);
    const ne = elementNarrationEnd(el, rendered);
    if (ns <= t + 0.02 && t < ne) activeEl = el;
  }
  if (activeEl) {
    const sc = mapped.scenes.find((s) =>
      (s.audioClips || []).some((c) => c.id === activeEl.id),
    );
    if (sc) {
      sceneIndex = sc.sceneIndex;
      blockIndex = (sc.audioClips || []).findIndex((c) => c.id === activeEl.id);
    }
  }

  return { sceneIndex, blockIndex };
}

/** Re-export for legacy imports */
export { buildSceneAudioTimeline } from './sceneAudioTimeline';
